<template>
  <div v-if="$apollo.queries.event.loading">
    <v-container>
      <AppLoadingPage />
    </v-container>
  </div>
  <div v-else-if="!event.id">
    <NoPage custom-text="Could not find the event from server" />
  </div>
  <div v-else>
    <div v-if="event.alertText" class="px-0" data-test="eventAlert">
      <AppAlertNotice :text="event.alertText" />
    </div>

    <v-container
      class="container-content pa-0 event-profile-page"
      style="padding-top: 0 !important; padding-bottom: 0"
    >
      <EventCardLarge :event="event">
        <template #icon-links>
          <div class="event-selections">
            <div>
              <v-tooltip location="bottom" :disabled="venueExists">
                <template #activator="{ props }">
                  <button
                    v-bind="props"
                    :class="[!venueExists ? 'disabled' : '', 'event-link']"
                    style="position: relative; pointer-events: initial"
                    data-test="venue"
                    @click="mapModal = true"
                  >
                    <div class="event-selections__content">
                      <v-icon color="rgba(0, 0, 0, 0.54)">$iconMap</v-icon>
                      <div>
                        {{ $t("events:EventCard.venue.label") }}
                      </div>
                    </div>
                  </button>
                </template>
                <span>{{
                  $t("events:EventCard.venueNotPresent.tooltip")
                }}</span>
              </v-tooltip>
            </div>

            <div>
              <component
                :is="!event.broadcastUrl ? 'button' : 'a'"
                :class="[!event.broadcastUrl ? 'disabled' : '', 'event-link']"
                :href="broadcastUrl"
                style="pointer-events: initial"
                target="_blank"
                data-test="broadcast"
              >
                <div class="event-selections__content">
                  <v-icon>$iconRecorder</v-icon>
                  <div>
                    {{
                      $t(
                        "events:EventPage.Live.navigation",
                        "Live",
                        "Link to live stream in event page"
                      )
                    }}
                  </div>
                </div>
              </component>
            </div>

            <div>
              <component
                :is="!event.homepageUrl ? 'button' : 'a'"
                :class="[!event.homepageUrl ? 'disabled' : '', 'event-link']"
                :href="homepageUrl"
                target="_blank"
                style="pointer-events: initial"
                data-test="homepage"
              >
                <div class="event-selections__content">
                  <v-icon>$iconGlobe</v-icon>
                  <div>
                    {{
                      $t(
                        "events:EventPage.Homepage.navigation",
                        "Homepage",
                        "Link to homepage in event page"
                      )
                    }}
                  </div>
                </div>
              </component>
            </div>
          </div>
        </template>
        <template #event-additional-details>
          <div>
            <AppShowEventCategories :event-categories="event.eventCategories" />
          </div>
          <span
            v-show="
              event.eventCategories.length > 0 &&
              event.profQualifications.length > 0
            "
            class="px-1"
          />
          <div>
            <AppShowProfQualificationsList
              :prof-qualifications="event.profQualifications"
            />
          </div>
        </template>
      </EventCardLarge>
    </v-container>
    <GoogleMapModal v-model="mapModal" :event="event" />

    <router-view style="min-height: 35rem" />
  </div>
</template>

<script>
import gql from "fraql";
import { ref, computed } from "vue";
import { useEventUrlHelpers } from "@/components/event/mixins/eventUrlMixin";

import AppShowEventCategories from "@AppShows/event/AppShowEventCategories.vue";
import AppShowProfQualificationsList from "@AppShows/AppShowProfQualificationsList.vue";
import EventCardLarge from "./EventCardLarge/index.vue";
import GoogleMapModal from "@elements/map/GoogleMapModal.vue";
import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import AppLoadingPage from "@elements/AppLoadingPage.vue";
import { DisplayTextFragments } from "@/composables/display_text";
import NoPage from "@/components/error_pages/404.vue";
import { checkVenueExists } from "./venueExists.ts";
import FipaListener from "@/utils/listenerUtility";
import { SentryCaptureError } from "@/utils/sentryErrorHandler";

export default {
  name: "NoncompetitionEventProfilePage",
  components: {
    EventCardLarge,
    AppAlertNotice,
    AppLoadingPage,
    NoPage,
    GoogleMapModal,
    AppShowEventCategories,
    AppShowProfQualificationsList
  },
  provide() {
    return {
      isNoncompetition: true
    };
  },
  props: {
    eventId: {
      type: String,
      default: null
    }
  },
  setup() {
    const event = ref({ id: null, cups: [], competitions: [] });
    const mapModal = ref(false);
    const { homepageUrl, broadcastUrl, startingListUrl } =
      useEventUrlHelpers(event);

    const venueExists = computed(() => {
      return checkVenueExists(event.value);
    });

    return {
      event,
      homepageUrl,
      broadcastUrl,
      startingListUrl,

      venueExists,
      mapModal
    };
  },
  apollo: {
    event: {
      query: gql`
        query noncompetitionEventProfile($id: ID) {
          event(id: $id) {
            id
            noncompetition
            name
            place
            eventCategoryIds
            eventCategories @client {
              ${AppShowEventCategories.fragments.eventCategoryFields}
            }
            profQualificationIds
            profQualifications @client {
              ${AppShowProfQualificationsList.fragments.profQualificationFields}
            }
            ${EventCardLarge.fragments.eventFields}
            description
            startDate
            endDate
            isHighlighted
            registrationEndDate
            alertText
            venueAddress
            venueLocationLatitude
            venueLocationLongitude
            broadcastUrl
            homepageUrl
            administrativeAlertMessages {
              ${DisplayTextFragments.translation}
            }
            regionId
            region @client {
              id
              name
            }
            countryId
            country @client {
              id
              name
              alpha2
              ioc
            }
            organiser {
              id
              name
              logoPath
              addressCountryId
              addressCity
              addressIndex
              addressText
              regionId
              region @client {
                id
                name
              }
            }
            notes {
              id
              content
              createdByPerson {
                id
                name
              }
              relatedAction
              createdAt
            }
            eventProducts {
              id
              paymentStructure
            }
            allowedMutations
          }
        }
      `,
      variables() {
        return {
          id: this.eventId
        };
      },
      manual: true,
      result({ loading, data }) {
        if (loading) return;
        if (data && (!data.event || !data.event.id)) {
          SentryCaptureError(Error("Noncompetition event was not found"), {
            level: "info",
            additionalDetails: {
              eventId: this.eventId,
              query: "noncompetitionEventProfile"
            }
          });
        } else if (data && data.event) {
          this.event = JSON.parse(JSON.stringify(data.event));
          if (!data.event.noncompetition) {
            SentryCaptureError(
              Error(
                "Page is for noncompetition event but server returned normal event"
              ),
              {
                level: "warning",
                additionalDetails: {
                  eventId: this.eventId,
                  query: "noncompetitionEventProfile"
                }
              }
            );
            this.$router.replace({
              name: "EventProfilePage",
              params: {
                eventId: data.event.id
              }
            });
            return;
          }
          FipaListener.emit("breadcrumbPageName", {
            name: data.event.name,
            id: data.event.id,
            breadcrumbName: "eventName"
          });
        }
      },
      fetchPolicy: "cache-first"
    }
  }
};
</script>
<style lang="scss">
.event-selections {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;

  @media (max-width: 960px) {
    justify-content: space-around;
  }

  .event-link {
    display: block;
    padding: 21px 0;
    min-width: 85px;
    margin: 0 21px;
    height: 75px;
    color: black !important;
    text-decoration: none;
    i {
      margin-bottom: 4px;
    }

    &:hover,
    &:focus {
      color: black !important;
      text-decoration: none;
    }

    &.disabled {
      pointer-events: none;
      color: grey;
      opacity: 0.5;
      cursor: not-allowed;
      &:focus {
        background: rgba(185, 185, 185, 0.15);
      }
    }

    @media (max-width: 960px) {
      min-width: 3.2em;
      margin: 0 6px;
      &:not(.disabled) {
        opacity: 1;

        .v-icon {
          color: black !important;
        }
      }
    }
  }

  &__content {
    text-align: center;
  }
}
</style>
